import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  ImageSourcePropType,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";

import {
  AnalyticsConst,
  AnalyticsInstance,
  AnalyticsPayloadGenerator,
  ComponentsModules,
  locales,
  OrderModules,
  RootState,
  StoreModules,
  StoreReduxAction,
} from "gyg_common";
import { IconButton, PrimaryBlackButton } from "gyg_common/components";
import editIcon from "gyg_common/components/assets/icons/icon_edit.png";
import deliveryIcon from "gyg_common/components/assets/icons/services/icon_delivery.png";
import TertiaryButton from "gyg_common/components/Buttons/TertiaryButton";
import {
  AbandonOrderModal,
  enableAbandonOrderModal,
} from "gyg_common/components/OrderSetup/AbandonOrderModal";
import { SetupScreenComponent } from "gyg_common/components/OrderSetup/OrderSetupFlowContent";
import { Spacing } from "gyg_common/components/styles/number";
import ToggleSelect, {
  ActiveTabType,
} from "gyg_common/components/ToggleSelect";
import config from "gyg_common/config";
import { useMediaQuery } from "gyg_common/hooks/useMediaQuery";
import {
  CollectionType,
  DeliveryModalState,
  DropOffOption,
  OrderType,
} from "gyg_common/redux_store/order/models";
import { basketValueSelector } from "gyg_common/redux_store/order/selectors";

import {
  DropdownMenu,
  DropdownMenuItem,
} from "../shared/DropdownMenu/DropdownMenu";

import colours from "@/styles/colours";
import { Typography } from "@/styles/typography";

const textStyles = (isDesktop: boolean) => {
  return StyleSheet.create({
    orderTypeText: {
      fontFamily: "Sini-Bold",
      fontSize: isDesktop ? 28 : 18,
      lineHeight: 1,
    },
  });
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    paddingHorizontal: Spacing.Regular,
    paddingBottom: Spacing.Regular,
  },
  contentContainer: {
    flex: 1,
    paddingBottom: Spacing.Regular,
  },
  orderTypeContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  rowCentered: { flexDirection: "row", alignItems: "center" },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colours.yellow,
    borderRadius: 100,
    width: 40,
    height: 40,
    marginRight: 10,
  },
  icon: { width: 30, height: 30 },
  infoContainer: {
    marginTop: Spacing.Regular,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  infoBoxContainer: {
    display: "flex",
    flexDirection: "column",
    rowGap: 10,
    flex: 1,
  },
  infoTitleText: {
    fontSize: 18,
    fontFamily: "Sini-Bold",
  },
  infoDescriptionText: {
    fontSize: 14,
    fontFamily: "Montserrat-Regular",
  },
  restaurantNameText: {
    fontSize: 15,
    fontFamily: "Montserrat-Medium",
  },
  space: {
    marginBottom: 16,
  },
  orderType: {
    ...Typography.body,
    paddingBottom: 8,
  },
  toggleButtonWrapper: {
    marginBottom: 16,
  },
  tableServiceWrapper: {
    marginTop: 17,
  },
  orderAsapSetting: {
    marginBottom: 20,
  },
  daySelectWrapper: {
    marginBottom: 20,
    zIndex: "auto",
  },
  timeSelectWrapper: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    zIndex: "auto",
  },
  timeDropdownWrapper: {
    flex: 1,
    paddingRight: 20,
    zIndex: "auto",
  },
  amPm: {
    flex: 1,
  },

  errorImageContainer: {
    flex: 1,
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  errorImage: {
    width: 150,
    height: 150,
  },
  errorMessage: {
    ...Typography.bodyBold,
    color: colours.red,
    marginTop: 8,
  },
});

const Screen: React.FC<SetupScreenComponent<DeliveryModalState>> = ({
  state,
  onBack,
  onBackToScreen,
  onChangeCollectionType,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { storeOrderTimeSlots } = useSelector(
    (state: RootState) => state.store
  );

  const { items } = useSelector((state: RootState) => state.cart);
  const analyticsState = useSelector((s: RootState) => s.analytics);
  const [abandonOrderModalVisible, setAbandonOrderModalVisible] =
    useState(false);

  const { isDesktopScreen } = useMediaQuery();
  const isWebDesktop = Platform.OS === "web" && isDesktopScreen;

  const basketValue = useSelector(basketValueSelector);

  const [isLoading, setIsLoading] = useState(true);

  // const { menuStructure, menuOpenTime } = useSelector(
  //   (state: RootState) => state.menu
  // );

  useEffect(() => {
    if (state.ChosenStore) {
      dispatch(
        StoreReduxAction.setStoreOrderTimeslotsAndOffset({
          storeId: state.ChosenStore.id,
          basketValue,
        })
      );
    }
  }, [state.ChosenStore, basketValue, dispatch]);

  const onSwitchToPickUp = () => {
    //Check if basket is empty or not
    AnalyticsInstance.trackEvent(
      AnalyticsConst.Events.ChangeToPickup,
      AnalyticsPayloadGenerator.storePayload(true, analyticsState)
    );
    if (items.length === 0 || !enableAbandonOrderModal) {
      onChangeCollectionType(CollectionType.PICK_UP, undefined, {
        ...state.ChosenStore,
      });
    } else {
      setAbandonOrderModalVisible(true);
    }
  };

  const onAbandonModalClose = () => {
    setAbandonOrderModalVisible(false);
  };

  const storeName =
    state.ChosenStore?.name?.length > 0 ? state.ChosenStore?.name : "";

  const addressText =
    state.ChosenStore?.address1?.length > 0
      ? state.ChosenStore?.address1
      : state.ChosenStore?.address2;
  const addressForDelivery = [
    ...(state.Business &&
    !state.DropoffAddress?.fullAddress.includes(state.Business)
      ? [state.Business]
      : []),
    [
      ...(state.Apartment &&
      !state.DropoffAddress?.fullAddress.includes(state.Apartment)
        ? [state.Apartment]
        : []),
      ...(state.DropoffAddress?.fullAddress
        ? [state.DropoffAddress?.fullAddress]
        : []),
    ].join("/"),
  ].join(", ");

  const [validOrderDays, setValidOrderDays] = useState<
    ComponentsModules.DropdownPickerModels.PickerItemProps<Moment>[]
  >([]);
  const [validOrderHoursAM, setValidOrderHoursAM] = useState<
    ComponentsModules.DropdownPickerModels.PickerItemProps<number>[]
  >([]);
  const [validOrderHoursPM, setValidOrderHoursPM] = useState<
    ComponentsModules.DropdownPickerModels.PickerItemProps<number>[]
  >([]);
  const [validOrderMinutes, setValidOrderMinutes] = useState<
    ComponentsModules.DropdownPickerModels.PickerItemProps<number>[]
  >([]);
  const [isAutoSetAMPM, setAutoSetAMPM] = useState<boolean>(false);

  const isAM = (amPmSetting: ActiveTabType) => {
    return amPmSetting === ActiveTabType.LEFT;
  };

  /* Catering */
  const [orderDay, setOrderDay] = useState<Moment>(moment());
  const [orderHour, setOrderHour] = useState(0);
  const [orderMinutes, setOrderMinutes] = useState(0);
  const [orderAmPm, setOrderAmPm] = useState(ActiveTabType.LEFT);
  const [orderTime] = useState<number | null>(null);

  const [bookedDayError, setBookedDayError] = useState(false);

  interface GroupAmPmTimeslotsProp {
    am: {
      // hour keys -> array of number for minutes valid in that hour
      [hour: number]: number[];
    };
    pm: {
      [hour: number]: number[];
    };
  }

  const [amPmTimeslots, setAmPmTimeslots] = useState<GroupAmPmTimeslotsProp>({
    am: {},
    pm: {},
  });

  const handleDaySelected = (selectedDayItem: DropdownMenuItem<Moment>) => {
    setBookedDayError(selectedDayItem.label.includes("Booked"));
    setOrderDay(selectedDayItem.value); // set the day for local UI state
  };
  const handleHourSelected = (selectedHourItem: DropdownMenuItem<number>) => {
    setOrderHour(selectedHourItem.value);
  };
  const handleMinuteSelected = (
    selectedMinuteItem: DropdownMenuItem<number>
  ) => {
    setOrderMinutes(selectedMinuteItem.value);
  };
  const handleAmPmSelected = (currentActive: ActiveTabType) => {
    const newAmPM =
      currentActive === ActiveTabType.LEFT
        ? ActiveTabType.RIGHT
        : ActiveTabType.LEFT;
    setOrderAmPm(newAmPM);
  };

  const [validHoursAM, setValidHoursAM] = useState<string[]>([]);
  const [validHoursPM, setValidHoursPM] = useState<string[]>([]);

  useEffect(() => {
    if (isLoading) return;

    // calculate the valid hour and minute slots for a given day
    const validHoursForDay = OrderModules.OrderUtils.getValidOrderTimeslots(
      orderDay,
      storeOrderTimeSlots.storeOrderTimes,
      new Date(),
      storeOrderTimeSlots.storeTimeZoneInfo.storeTimeZone
    );

    setAmPmTimeslots(
      OrderModules.OrderUtils.groupAmPmTimeslots(validHoursForDay)
    );

    setValidHoursAM(Object.keys(amPmTimeslots.am));
    setValidHoursPM(Object.keys(amPmTimeslots.pm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, orderDay]);

  useEffect(() => {
    if (isLoading) return;

    let validDays = OrderModules.OrderUtils.getValidOrderSetupDays(
      storeOrderTimeSlots.storeOrderTimes
    ) as ComponentsModules.DropdownPickerModels.PickerItemProps<Moment>[];

    // 48 hours in advance
    validDays.shift();
    validDays.shift();

    validDays = validDays.map((day) => {
      if (
        state.ChosenStore?.bookedCateringDates.includes(
          day.value.format("YYYY-MM-DD")
        )
      ) {
        return {
          label: day.label + " (Booked)",
          value: day.value,
        };
      }
      return day;
    });

    setValidOrderDays(validDays);
    setOrderDay(
      orderTime
        ? moment(orderTime).tz(
            storeOrderTimeSlots.storeTimeZoneInfo.storeTimeZone
          )
        : validDays.length
          ? validDays[0].value
          : moment().tz(storeOrderTimeSlots.storeTimeZoneInfo.storeTimeZone)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, orderTime]);

  useEffect(() => {
    if (isLoading) return;

    const hoursForAM = OrderModules.OrderUtils.orderDisplayedHours(
      validHoursAM
    ).map((hour) => {
      return { label: hour, value: Number(hour) };
    });
    const hoursForPM = OrderModules.OrderUtils.orderDisplayedHours(
      validHoursPM
    ).map((hour) => {
      return { label: hour, value: Number(hour) };
    });

    setValidOrderHoursAM(hoursForAM);
    setValidOrderHoursPM(hoursForPM);

    if (!orderTime) {
      setOrderHour(
        validHoursAM.length ? Number(validHoursAM[0]) : Number(validHoursPM[0])
      );
    }

    //reset auto setting AM PM flag if day/menu is changed
    setAutoSetAMPM(false);

    // TODO: To check and revist later
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    isLoading,
    validOrderDays,
    orderDay,
    orderTime,
    validHoursAM,
    validHoursPM,
  ]);

  // calculate the valid minutes to populate in minute picker and update when day, hour  or AM/PM changes
  useEffect(() => {
    if (isLoading) return;

    if (orderDay && orderHour) {
      const timeSlots = isAM(orderAmPm) ? amPmTimeslots.am : amPmTimeslots.pm;
      const validMinutes = timeSlots[orderHour] ?? [];

      setValidOrderMinutes(
        validMinutes.map((minute) => {
          return {
            label: minute < 10 ? `0${minute.toString()}` : minute.toString(),
            value: minute,
          };
        })
      );

      if (validMinutes.length > 0) {
        setOrderMinutes(validMinutes[0]);
      } else {
        const defaultHour = Number(
          isAM(orderAmPm) ? validHoursAM[0] : validHoursPM[0]
        );
        if (!isNaN(defaultHour)) {
          setOrderHour(defaultHour);
          setOrderMinutes(timeSlots[defaultHour][0]);
        }
      }
    }
    // TODO: To check and revist later
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isLoading, orderDay, orderHour, orderAmPm, orderTime]);

  /**
   * Runs only once (isAutoSetAMPM)
   * Auto set AM PM if user has not confirmed order setup
   */
  useEffect(() => {
    if (isLoading) return;
    if (!isAutoSetAMPM) {
      if (!validOrderHoursAM.length) {
        setOrderAmPm(ActiveTabType.RIGHT);
      } else {
        setOrderAmPm(ActiveTabType.LEFT);
      }
      setAutoSetAMPM(true);
    }
  }, [isLoading, isAutoSetAMPM, validOrderHoursAM]);

  useEffect(() => {
    if (
      storeOrderTimeSlots.storeOrderTimes &&
      storeOrderTimeSlots.storeTimeZoneInfo
    ) {
      setIsLoading(false);
    }
  }, [storeOrderTimeSlots]);

  const getOrderTime = useCallback(() => {
    if (isLoading) return;
    const hour = StoreModules.StoreUtils.convertHourTo24format(
      isAM(orderAmPm),
      orderHour
    );
    const timestamp = moment()
      .tz(storeOrderTimeSlots.storeTimeZoneInfo.storeTimeZone)
      .set("hour", hour)
      .set("minute", orderMinutes)
      .set("second", 0)
      .set("date", orderDay.get("date"))
      .set("month", orderDay.get("month"))
      .toDate();

    return timestamp.getTime();
  }, [isLoading, orderDay, orderHour, orderMinutes, orderAmPm]);

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <div style={styles.orderTypeContainer}>
          <View style={styles.rowCentered}>
            <View style={styles.iconContainer}>
              <Image
                source={deliveryIcon as ImageSourcePropType}
                style={styles.icon}
                resizeMode='contain'
              />
            </View>
            <div style={textStyles(isWebDesktop).orderTypeText}>
              {t(
                state.OrderType !== OrderType.CATERING
                  ? "OrderManagement:delivery"
                  : "Catering Delivery"
              ).toUpperCase()}
            </div>
          </View>
          <div>
            <TertiaryButton
              onClick={onSwitchToPickUp}
              title={t("OrderManagement:changeToPickup")}
              bgColor={colours.borderGrey}
            />
          </div>
        </div>
        <div className='grey-line divider' />
        <div style={styles.infoContainer}>
          <div style={styles.infoBoxContainer}>
            <div style={styles.infoTitleText}>
              {t("OrderManagement:deliveryDetails")}
            </div>
            <div
              style={styles.infoDescriptionText}
              dangerouslySetInnerHTML={{ __html: addressForDelivery }}
            />
            <div style={styles.infoDescriptionText}>
              {state.DropOffOptionTitle}
              {state.DropOffOption === DropOffOption.MEET_AT_DOOR &&
                state.DropoffPinCodeRequired && (
                  <>
                    <br />
                    {t("OrderManagement:dropoffPinCodeRequired")}
                  </>
                )}
              {state.Instructions && (
                <>
                  <br />
                  {`${state.Instructions}`}
                </>
              )}
            </div>
          </div>
          <IconButton
            withShadow={false}
            onPress={() => {
              onBackToScreen("DeliveryAddress");
            }}
            iconImage={editIcon}
          />
        </div>
        <div className='grey-line divider' />
        <div style={styles.infoContainer}>
          <div style={styles.infoBoxContainer}>
            <div style={styles.infoTitleText}>
              {t("OrderManagement:gygRestaurant")}
            </div>
            <div style={styles.restaurantNameText}>{storeName}</div>
            <div style={styles.infoDescriptionText}>{addressText}</div>
          </div>
          <div>
            <IconButton
              withShadow={false}
              onPress={() => {
                onBack(state);
              }}
              iconImage={editIcon}
            />
          </div>
        </div>

        {state.OrderType === OrderType.CATERING && !isLoading && (
          <>
            <div className='grey-line divider' />
            <div style={styles.infoContainer}>
              <div style={styles.infoBoxContainer}>
                <div style={styles.infoTitleText}>
                  Select a date for catering
                </div>
                <div style={styles.restaurantNameText}>
                  Order 48hrs in advance
                </div>
                <div
                  style={{
                    width: "50%",
                  }}>
                  <View style={styles.daySelectWrapper}>
                    <DropdownMenu
                      items={validOrderDays}
                      isError={bookedDayError}
                      selectedItem={{
                        label: orderDay.format(
                          config.version === locales.US
                            ? "dddd, MMMM DD"
                            : "dddd DD MMMM"
                        ),
                        value: orderDay,
                      }}
                      onItemSelected={handleDaySelected}
                      dropdownDirection={"top"}
                    />
                  </View>
                  <View style={styles.timeSelectWrapper}>
                    <View style={styles.timeDropdownWrapper}>
                      <DropdownMenu
                        items={
                          isAM(orderAmPm)
                            ? validOrderHoursAM
                            : validOrderHoursPM
                        }
                        selectedItem={
                          orderHour // There should be a case where AM is auto switched to PM if no valid orderHours come back
                            ? {
                                label: orderHour.toString(),
                                value: orderHour,
                              }
                            : undefined
                        }
                        onItemSelected={handleHourSelected}
                        dropdownDirection={"top"}
                      />
                    </View>
                    <View style={styles.timeDropdownWrapper}>
                      <DropdownMenu
                        items={validOrderMinutes}
                        selectedItem={
                          orderMinutes !== undefined
                            ? {
                                label:
                                  orderMinutes < 10
                                    ? `0${orderMinutes.toString()}`
                                    : orderMinutes.toString(),
                                value: orderMinutes,
                              }
                            : undefined
                        }
                        onItemSelected={handleMinuteSelected}
                        dropdownDirection={"top"}
                      />
                    </View>
                    <View style={styles.amPm}>
                      <ToggleSelect
                        onUpdateTab={handleAmPmSelected}
                        activeTab={orderAmPm}
                        leftTitle={"AM"}
                        rightTitle={"PM"}
                      />
                    </View>
                  </View>
                </div>
              </div>
            </div>
          </>
        )}
      </View>

      <PrimaryBlackButton
        onClick={onSubmit.bind(this, { ...state, orderTime: getOrderTime() })}
        buttonName={t("OrderManagement:confirm")}
      />

      <AbandonOrderModal
        isModalVisible={abandonOrderModalVisible}
        isDelivery={true}
        abandonOrder={onChangeCollectionType.bind(
          this,
          CollectionType.PICK_UP,
          undefined,
          { ...state.ChosenStore }
        )}
        keepCart={onAbandonModalClose}
        onModalClose={onAbandonModalClose}
      />
    </View>
  );
};

export default Screen;
